import React, { useCallback, useEffect, useMemo, useRef } from "react";
import Footer from "../../components/ui/Footer";
import Navbar from "../../components/ui/Navbar";
import styles from "./Renew.module.css";
import { useDispatch, useSelector } from "react-redux";
import FixedAgent from "../../utils/api/FixedAgent";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router";
import { userDataActions } from "../../store/redux-store/Index";
import Auth from "../../utils/api/Auth";
import { parseMonthsFromExpiration } from "../../utils/parseMonthsFromExpiration";
import Papa from "papaparse";
import { CEI_MAKES, NICON_MAKES } from "../../utils/constants/vehicleData";
import { showSuccessToast, showErrorToast } from "../../utils/toastFactory";
import { useMakeModelLookup } from "../../hooks/useMakeModelLookup";
import { useVehicleData } from "../../hooks/useVehicleData";
import { useFormData } from "../../hooks/useFormData";
import { useAutofillTracking } from "../../hooks/useAutofillTracking";
import { mergeVehicleInfo } from "../../utils/vehicleInfo";

// Sub-components
import WarningModal from "../../components/renewal/WarningModal";
import DetailsModal from "../../components/renewal/DetailsModal";
import VehicleFormFields from "../../components/renewal/VehicleFormFields";

const EMPTY_SALAM_MAKES = [];
const EMPTY_AG_VEHICLE_MAKE_MODELS = [];
const EMPTY_FGI_MAKES = [];
const EMPTY_FGI_COLORS = [];
const EMPTY_FGI_MODELS = [];

const normalizeText = (value) =>
  value?.toString().toLowerCase().replace(/\s+/g, " ").trim() || "";

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDateTwoMonthsAhead = (currentDate) => {
  const current = new Date(`${currentDate}T00:00:00`);
  const targetMonth = current.getMonth() + 2;
  const targetYear = current.getFullYear() + Math.floor(targetMonth / 12);
  const normalizedTargetMonth = targetMonth % 12;
  const lastDayOfTargetMonth = new Date(
    targetYear,
    normalizedTargetMonth + 1,
    0,
  ).getDate();

  return formatDateInputValue(
    new Date(
      targetYear,
      normalizedTargetMonth,
      Math.min(current.getDate(), lastDayOfTargetMonth),
    ),
  );
};

const getFocuswellProviderMessage = (error) => {
  const debug = error?.response?.data?.debug;

  if (!debug) return "";

  try {
    const providerError =
      typeof debug === "string" ? JSON.parse(debug) : debug;
    return providerError?.message || "";
  } catch {
    return "";
  }
};

const parseAgVehicleMakeModel = (value) => {
  if (!value) {
    return { make: "", model: "" };
  }

  const match = value.match(/^(.*?):\s*\[(.*)\]$/);

  if (!match) {
    return { make: value.trim(), model: "" };
  }

  return {
    make: match[1].trim(),
    model: match[2].trim(),
  };
};

const buildAgVehicleMakeModelLabel = (makeName, modelName) => {
  if (!makeName || !modelName) return "";
  return `${makeName.trim()}: [${modelName.trim()}]`;
};

const getAgProductCategory = (productCategory) => {
  const normalizedCategory = String(productCategory || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");

  if (normalizedCategory.includes("private")) return "private";
  if (normalizedCategory.includes("commercial")) return "commercial";
  if (normalizedCategory.includes("motorcycle")) return "motorcycle";
  if (normalizedCategory.includes("tricycle")) return "tricycle";
  return "";
};

const matchesAgProductCategory = (option, category, optionKind) => {
  const id = String(option?.id || "").toUpperCase();
  const name = String(option?.name || "").toLowerCase();

  if (optionKind === "vehicle") {
    return {
      private: name.includes("private motor only"),
      commercial: name.includes("commercial only"),
      motorcycle: name.includes("motorcycle only"),
      tricycle: name.includes("tricycle only"),
    }[category];
  }

  return {
    private: ["PMI", "PMC"].includes(id),
    commercial: id.startsWith("CV"),
    motorcycle: id.startsWith("MC"),
    tricycle: id.startsWith("TC"),
  }[category];
};

function RenewInfo() {
  const isSubmittingRef = useRef(false);
  const hasAutoLookedUpRef = useRef(false);
  const hasRetriedAfterCustomerRegistrationRef = useRef(false);
  const currentDate = new Date().toISOString().split("T")[0];

  // --- Redux state ---
  const user = useSelector((state) => state.userData.personalInfo);
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userData.loginData.token);
  const renewData = useSelector((state) => state.userData.renewPapersData);
  const salamMeta = useSelector((state) => state.userData.salamMeta.data);
  const agMeta = useSelector((state) => state.userData.agMeta.data);
  const makeModelMapping = useSelector(
    (state) => state.userData.makeModelMapping,
  );
  const navigate = useNavigate();

  const vehicleInfo = useMemo(
    () =>
      renewData?.vehicleInfo ||
      mergeVehicleInfo(
        renewData?.getNiconInsuranceRes,
        renewData?.autoregVehicleInfo,
      ),
    [
      renewData?.autoregVehicleInfo,
      renewData?.getNiconInsuranceRes,
      renewData?.vehicleInfo,
    ],
  );

  // --- Constants ---
  const ceiMake = CEI_MAKES;
  const niconMake = NICON_MAKES;
  const salamMake = salamMeta?.makes || EMPTY_SALAM_MAKES;
  const agVehicleMakeModels =
    agMeta?.vehicleMakeModels || EMPTY_AG_VEHICLE_MAKE_MODELS;
  const agpVehicleMakes = agMeta?.agpMakes || [];
  const agpVehicleModels = agMeta?.agpModels || [];
  const isNicon = renewData.insuranceType === "NICON";
  const isSalam = renewData.insuranceType === "SALAM";
  const isAg = renewData.insuranceType === "AG";
  const isAgp = renewData.insuranceType === "AGP";
  const isAgFamily = isAg || isAgp;
  const isFgi = renewData.insuranceType === "FGI";
  const isFgip = renewData.insuranceType === "FGIP";
  const isFgiFamily = isFgi || isFgip;
  const agMaxEffectiveDate = getDateTwoMonthsAhead(currentDate);
  const agProductCategory = getAgProductCategory(
    renewData?.productsDetails?.product_category,
  );
  const agVehicleTypes = isAgp
    ? agMeta?.agpVehicleTypes || []
    : agMeta?.vehicleTypes || [];
  const agPolicyTypes = isAgp
    ? agMeta?.agpTypes || []
    : agMeta?.types || [];
  const lookupVehicleMake = String(vehicleInfo?.make || "").trim();
  const lookupVehicleModel = String(vehicleInfo?.model || "").trim();

  // --- Custom Hooks ---
  const { input, setInput } = useFormData();

  const agVehicleTypeOptions = useMemo(
    () =>
      agVehicleTypes.filter((option) =>
        matchesAgProductCategory(option, agProductCategory, "vehicle"),
      ),
    [agProductCategory, agVehicleTypes],
  );
  const agPolicyTypeOptions = useMemo(
    () =>
      agPolicyTypes.filter((option) =>
        matchesAgProductCategory(option, agProductCategory, "policy"),
      ),
    [agPolicyTypes, agProductCategory],
  );

  useEffect(() => {
    if (!isAgFamily) return;

    setInput((previousInput) => {
      if (!previousInput.agVehicleType && !previousInput.agPolicyType) {
        return previousInput;
      }

      return {
        ...previousInput,
        agVehicleType: "",
        agPolicyType: "",
      };
    });
  }, [agProductCategory, isAgFamily, setInput]);

  useEffect(() => {
    if (isFgiFamily && input.effectiveDate !== currentDate) {
      setInput((previousInput) => ({
        ...previousInput,
        effectiveDate: currentDate,
      }));
    }
  }, [currentDate, input.effectiveDate, isFgiFamily, setInput]);
  const [fgiFormData, setFgiFormData] = React.useState(null);
  const [fgiFormDataLoading, setFgiFormDataLoading] = React.useState(false);
  const [fgiFormDataError, setFgiFormDataError] = React.useState(null);
  const [fgiFormDataRetryKey, setFgiFormDataRetryKey] = React.useState(0);
  const [fgipMakes, setFgipMakes] = React.useState([]);
  const [fgipModels, setFgipModels] = React.useState([]);
  const [fgipDataLoading, setFgipDataLoading] = React.useState(false);
  const [fgipDataError, setFgipDataError] = React.useState(null);
  const fgiMakes = fgiFormData?.vehicle_makes || EMPTY_FGI_MAKES;
  const fgiVehicleColors = useMemo(
    () =>
      (fgiFormData?.colors || EMPTY_FGI_COLORS).map((color) => ({
        ...color,
        vehicleColorId: color.id,
        colorName: color.name,
      })),
    [fgiFormData?.colors],
  );
  const fgiVehicleModels = useMemo(() => {
    const selectedMake = fgiMakes.find(
      (make) => Number(make.id) === Number(input.make),
    );
    return selectedMake?.models || EMPTY_FGI_MODELS;
  }, [fgiMakes, input.make]);
  const vehicleMakes = isFgip ? fgipMakes : fgiMakes;
  const fgiFamilyVehicleModels = isFgip ? fgipModels : fgiVehicleModels;
  const selectedAgVehicleMakeModel = agVehicleMakeModels.find(
    (option) => option.id === input.agVehicleMakeModelId,
  );
  const parsedAgVehicle = parseAgVehicleMakeModel(
    selectedAgVehicleMakeModel?.name,
  );
  const agVehicleMakeModelOptions = useMemo(() => {
    if (!isAg || !lookupVehicleMake) {
      return agVehicleMakeModels;
    }

    const filteredOptions = agVehicleMakeModels.filter(
      (option) =>
        normalizeText(parseAgVehicleMakeModel(option.name).make) ===
        normalizeText(lookupVehicleMake),
    );

    return filteredOptions.length > 0 ? filteredOptions : agVehicleMakeModels;
  }, [agVehicleMakeModels, isAg, lookupVehicleMake]);
  const agpVehicleModelOptions = useMemo(() => {
    if (!input.make) return [];

    const selectedMake = agpVehicleMakes.find(
      (make) => String(make.id) === String(input.make),
    );
    const selectedMakeName = normalizeText(selectedMake?.name || input.make);

    return agpVehicleModels.filter((model) => {
      const modelMake =
        model.make_id || model.makeId || model.make || model.vehicle_make || "";
      if (modelMake) {
        return (
          String(modelMake) === String(input.make) ||
          normalizeText(modelMake) === selectedMakeName
        );
      }

      return (
        normalizeText(String(model.name || "").split(/:\s*\(/)[0]) ===
        selectedMakeName
      );
    });
  }, [agpVehicleMakes, agpVehicleModels, input.make]);

  const {
    lookupMakeId,
    lookupModelId,
    makeIdLoading,
    makeIdStatus,
    modelIdLoading,
    modelIdStatus,
  } = useMakeModelLookup(
    makeModelMapping,
    renewData,
    ceiMake,
    niconMake,
    salamMeta,
    token,
  );

  const {
    vehicleColor: providerVehicleColor,
    vehicleModel: providerVehicleModel,
    yearRange,
  } = useVehicleData(
    input.make,
    makeModelMapping,
    renewData,
    ceiMake,
    niconMake,
    salamMeta,
    agMeta,
    token,
  );
  const vehicleColor = isFgiFamily ? fgiVehicleColors : providerVehicleColor;
  const vehicleModel = isFgiFamily
    ? fgiFamilyVehicleModels
    : providerVehicleModel;
  const getConfirmedCustomerVehicle = useCallback(() => {
    const make = isAg
      ? parsedAgVehicle.make
      : isAgp
        ? agpVehicleMakes.find(
            (option) => String(option.id) === String(input.make),
          )?.name || input.make
      : isFgiFamily
        ? vehicleMakes.find((option) =>
            isFgip
              ? String(option.code) === String(input.make)
              : Number(option.id) === Number(input.make),
          )
            ?.name || input.make
        : ceiMake.find((option) => option.id === input.make)?.name ||
          niconMake.find((option) => option.id === input.make)?.name ||
          salamMake.find((option) => option.id === input.make)?.name ||
          input.make;

    const model = isAg
      ? parsedAgVehicle.model
      : isAgp
        ? agpVehicleModels.find(
            (option) => String(option.id) === String(input.model),
          )?.name || input.model
      : isFgiFamily
        ? vehicleModel.find((option) => Number(option.id) === Number(input.model))
            ?.name || input.model
        : isSalam
          ? vehicleModel.find((option) => option.id === input.model)?.name
          : renewData.insuranceType !== "NICON"
            ? vehicleModel.find(
                (option) => option.vehicleModelId === Number(input.model),
              )?.modelName
            : vehicleModel.find((option) => option.id === input.model)?.name;

    const color =
      vehicleColor.find((option) => {
        const optionValue = isNicon
          ? option.colorName
          : option.vehicleColorId;
        return String(optionValue) === String(input.color);
      })?.colorName || input.color;

    return { make, model, color };
  }, [
    ceiMake,
    agpVehicleMakes,
    agpVehicleModels,
    fgiMakes,
    fgipMakes,
    input.color,
    input.make,
    input.model,
    isAg,
    isAgp,
    isFgiFamily,
    isFgip,
    isNicon,
    isSalam,
    niconMake,
    parsedAgVehicle.make,
    parsedAgVehicle.model,
    renewData.insuranceType,
    salamMake,
    vehicleColor,
    vehicleMakes,
    vehicleModel,
  ]);

  // --- Local state for UI ---
  const [isLoading, setIsLoading] = React.useState(false);
  const [commercialType, setCommercialType] = React.useState("");
  const [modalOpen, setModalOpen] = React.useState(false);
  const [darakCheckResult, setDarakCheckResult] = React.useState(null);
  const [showWarningModal, setShowWarningModal] = React.useState(false);

  // FGI uses its own make/model/colour metadata. FGIP reuses only its colours;
  // Focuswell makes and models are loaded from their dedicated endpoints below.
  useEffect(() => {
    if (!isFgiFamily) return;

    let isActive = true;

    const fetchFgiFormData = async () => {
      try {
        setFgiFormDataLoading(true);
        setFgiFormDataError(null);
        const response = await FixedAgent.getFgiFormData({ token });
        const formData = response?.data?.data;

        if (isFgip ? !formData?.colors : !formData?.vehicle_makes || !formData?.colors) {
          throw new Error(
            `Invalid ${isFgip ? "FGIP colour" : "FGI form"} data response`,
          );
        }

        if (isActive) setFgiFormData(formData);
      } catch (error) {
        if (isActive) {
          setFgiFormDataError(
            error?.response?.data?.message ||
              error?.message ||
              "Failed to load FGI vehicle data",
          );
        }
      } finally {
        if (isActive) setFgiFormDataLoading(false);
      }
    };

    fetchFgiFormData();

    return () => {
      isActive = false;
    };
  }, [isFgiFamily, isFgip, fgiFormDataRetryKey, token]);

  useEffect(() => {
    if (!isFgip) return;

    let isActive = true;
    const fetchFgipMakes = async () => {
      try {
        setFgipDataLoading(true);
        setFgipDataError(null);
        const response = await FixedAgent.getFgipMakes({ token });
        const makes = response?.data?.data || response?.data || [];

        if (!Array.isArray(makes)) {
          throw new Error("Invalid FGIP make data response");
        }

        if (isActive) setFgipMakes(makes);
      } catch (error) {
        if (isActive) {
          setFgipDataError(
            error?.response?.data?.message ||
              error?.message ||
              "Failed to load FGIP vehicle makes",
          );
        }
      } finally {
        if (isActive) setFgipDataLoading(false);
      }
    };

    fetchFgipMakes();
    return () => {
      isActive = false;
    };
  }, [fgiFormDataRetryKey, isFgip, token]);

  useEffect(() => {
    if (!isFgip || !input.make) {
      setFgipModels([]);
      return;
    }

    let isActive = true;
    const fetchFgipModels = async () => {
      try {
        setFgipDataLoading(true);
        setFgipDataError(null);
        const response = await FixedAgent.getFgipVehicleModels({
          makeCode: input.make,
          token,
        });
        const models = response?.data?.data || response?.data || [];

        if (!Array.isArray(models)) {
          throw new Error("Invalid FGIP model data response");
        }

        if (isActive) setFgipModels(models);
      } catch (error) {
        if (isActive) {
          setFgipDataError(
            error?.response?.data?.message ||
              error?.message ||
              "Failed to load FGIP vehicle models",
          );
        }
      } finally {
        if (isActive) setFgipDataLoading(false);
      }
    };

    fetchFgipModels();
    return () => {
      isActive = false;
    };
  }, [input.make, isFgip, token]);

  // --- Autofill Performance Tracking ---
  useAutofillTracking(input, hasAutoLookedUpRef.current);

  // --- Fetch Make/Model mapping from Google Sheets ---
  useEffect(() => {
    const fetchMapping = async () => {
      // Re-fetch if data is stale (older than 1 hour) or not yet loaded
      const STALE_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour
      const isStale =
        makeModelMapping.lastFetched &&
        Date.now() - new Date(makeModelMapping.lastFetched).getTime() >
          STALE_THRESHOLD_MS;

      if (makeModelMapping.loading) return;
      if (makeModelMapping.data.length > 0 && !isStale) return;

      const sheetUrl =
        process.env.REACT_APP_MAKE_MODEL_SHEET_URL ||
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vS1DFB_KZAT74UewtAT7GUG-gQ2LLx4ELHifzknndGhQgza7Gl55EKfARR-JDK1k3HFJ0SQmzJTWa07/pub?output=csv";

      if (!sheetUrl) return;

      try {
        dispatch(userDataActions.setMakeModelMappingLoading(true));

        const response = await FixedAgent.fetchMakeModelSpreadsheet({
          sheetUrl,
        });

        const parsedData = Papa.parse(response.data, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header) => header.trim(),
        });

        if (parsedData.errors.length > 0) {
          throw new Error("Failed to parse spreadsheet data");
        }

        dispatch(userDataActions.setMakeModelMappingData(parsedData.data));
      } catch (error) {
        dispatch(
          userDataActions.setMakeModelMappingError(
            error?.message || "Failed to load Make/Model data",
          ),
        );
        showErrorToast(
          "Unable to load Make/Model data. Will proceed to fallback data.",
        );
      }
    };

    fetchMapping();
  }, [
    dispatch,
    makeModelMapping.data.length,
    makeModelMapping.loading,
    makeModelMapping.lastFetched,
  ]);

  // --- Auto-lookup Make/Model from store data ---
  useEffect(() => {
    const autoLookupFromStoreData = async () => {
      if (isFgiFamily) {
        if (
          hasAutoLookedUpRef.current ||
          !vehicleMakes.length
        ) {
          return;
        }

        const fgiLookupMake = lookupVehicleMake;

        if (!fgiLookupMake) return;

        hasAutoLookedUpRef.current = true;
        const matchedMake = vehicleMakes.find(
          (make) => normalizeText(make.name) === normalizeText(fgiLookupMake),
        );

        if (!matchedMake) return;

        const matchedModel = isFgip
          ? null
          : matchedMake.models?.find(
              (model) =>
                normalizeText(model.name) === normalizeText(lookupVehicleModel),
            );

        setInput((prev) => ({
          ...prev,
          make: isFgip ? matchedMake.code : matchedMake.id,
          ...(!isFgip && matchedModel ? { model: matchedModel.id } : {}),
        }));
        return;
      }

      if (!isSalam && !isAgFamily && makeModelMapping.data.length === 0) return;
      if (hasAutoLookedUpRef.current) return;

      if (!lookupVehicleMake) return;

      try {
        hasAutoLookedUpRef.current = true;

        if (isAg) {
          if (agVehicleMakeModels.length === 0) {
            hasAutoLookedUpRef.current = false;
            return;
          }

          if (!lookupVehicleModel) {
            return;
          }

          const combinedVehicleLabel = buildAgVehicleMakeModelLabel(
            lookupVehicleMake,
            lookupVehicleModel,
          );
          const matchedVehicle = agVehicleMakeModels.find(
            (option) =>
              normalizeText(option.name) === normalizeText(combinedVehicleLabel),
          );

          if (matchedVehicle?.id) {
            setInput((prev) => ({
              ...prev,
              agVehicleMakeModelId: matchedVehicle.id,
            }));
          }

          return;
        }

        if (isAgp) {
          const matchedMake = agpVehicleMakes.find(
            (make) => normalizeText(make.name) === normalizeText(lookupVehicleMake),
          );
          const matchedModel = agpVehicleModels.find((model) => {
            const modelMake =
              model.make_id || model.makeId || model.make || model.vehicle_make || "";
            const modelName = String(model.name || "");
            const modelLabel = modelName.match(/:\s*\((.*)\)$/)?.[1] || modelName;

            return (
              normalizeText(modelLabel) === normalizeText(lookupVehicleModel) &&
              (!modelMake ||
                String(modelMake) === String(matchedMake?.id) ||
                normalizeText(modelMake) === normalizeText(matchedMake?.name))
            );
          });
          if (matchedMake?.id) {
            setInput((prev) => ({
              ...prev,
              make: matchedMake.id,
              model: matchedModel?.id || "",
            }));
          }
          return;
        }

        if (lookupVehicleMake) {
          const makeId = await lookupMakeId(lookupVehicleMake);
          if (makeId) {
            setInput((prev) => ({ ...prev, make: makeId }));
          }
        }

        if (lookupVehicleModel && lookupVehicleMake) {
          const modelId = await lookupModelId(
            lookupVehicleModel,
            lookupVehicleMake,
          );
          if (modelId) {
            setInput((prev) => ({ ...prev, model: modelId }));
          }
        }
      } catch (error) {
        hasAutoLookedUpRef.current = false;
      }
    };

    autoLookupFromStoreData();
  }, [
    isSalam,
    isAg,
    isAgp,
    isAgFamily,
    isFgiFamily,
    vehicleMakes,
    isFgip,
    makeModelMapping.data.length,
    agVehicleMakeModels,
    agpVehicleMakes,
    agpVehicleModels,
    lookupVehicleMake,
    lookupVehicleModel,
    lookupMakeId,
    lookupModelId,
    salamMeta,
    setInput,
  ]);

  // --- Handle input changes with Make/Model ID enrichment ---
  const handleInputChange = useCallback(
    async (e) => {
      const { name, value } = e.target;

      if (isFgiFamily) {
        setInput((prev) => ({
          ...prev,
          [name]: value,
          ...(name === "make" ? { model: "" } : {}),
        }));
        return;
      }

      if (isAgFamily) {
        if (isAgp && name === "make") {
          setInput((prev) => ({ ...prev, make: value, model: "" }));
        } else {
          setInput((prev) => ({ ...prev, [name]: value }));
        }
        return;
      }

      setInput((prev) => ({ ...prev, [name]: value }));

      // Trigger Make ID lookup when Make is selected
      if (name === "make" && value) {
        const makeArray =
          renewData.insuranceType === "NICON"
            ? niconMake
            : renewData.insuranceType === "SALAM"
              ? salamMake
              : ceiMake;
        const selectedMake = makeArray.find((make) => make.id === value);

        if (selectedMake) {
          // Run the spreadsheet lookup for status feedback (toast),
          // but keep the original dropdown value so the select field displays correctly.
          // useVehicleData resolves models by name matching, so models still load.
          await lookupMakeId(selectedMake.name);
          return;
        }
      }

      // Trigger Model ID lookup when Model is selected
      if (name === "model" && value) {
        const selectedModel = vehicleModel.find((model) =>
          renewData.insuranceType === "NICON" ||
          renewData.insuranceType === "SALAM" ||
          renewData.insuranceType === "AG"
            ? model.id === value
            : model.vehicleModelId === value,
        );

        const makeArray =
          renewData.insuranceType === "NICON"
            ? niconMake
            : renewData.insuranceType === "SALAM"
              ? salamMake
              : ceiMake;
        const selectedMake = makeArray.find((make) => make.id === input.make);

        if (selectedModel && selectedMake) {
          let finalModelId = selectedModel.id;

          const spreadSheetId = await lookupModelId(
            selectedModel.name || selectedModel.modelName,
            selectedMake.name,
          );
          if (spreadSheetId) {
            finalModelId = spreadSheetId;
          }

          setInput((prev) => ({ ...prev, [name]: finalModelId }));
          return;
        }
      }
    },
    [
      setInput,
      renewData.insuranceType,
      isAgFamily,
      isAgp,
      isFgiFamily,
      niconMake,
      ceiMake,
      salamMake,
      vehicleModel,
      input.make,
      lookupMakeId,
      lookupModelId,
    ],
  );

  // --- Prefill form from renewData ---
  useEffect(() => {
    if (renewData) {
      setInput((prev) => ({
        ...prev,
        fullname:
          renewData?.getNiconInsuranceRes?.insured_name ||
          renewData?.getNiconInsuranceRes?.full_name ||
          renewData?.getNiconInsuranceRes?.insured,
        phone:
          renewData?.getNiconInsuranceRes?.phone ||
          renewData?.getNiconInsuranceRes?.phone_number ||
          renewData?.getNiconInsuranceRes?.phone_no ||
          user?.phone_no ||
          renewData?.getNiconInsuranceRes?.phoneNumber,
        chasisNo:
          renewData?.getNiconInsuranceRes?.chasis_no ||
          renewData?.getNiconInsuranceRes?.chassis_no ||
          renewData?.getNiconInsuranceRes?.chassis ||
          vehicleInfo.chassis,
        engineNo:
          renewData?.getNiconInsuranceRes?.engine_no ||
          renewData?.getNiconInsuranceRes?.engineNo,
        email:
          renewData?.getNiconInsuranceRes?.email ||
          renewData?.getNiconInsuranceRes?.email_address ||
          user?.email,
        plate_no: renewData?.plateNumber,
      }));
    }
  }, [renewData, user?.email, user?.phone_no, setInput, vehicleInfo.chassis]);

  // --- Derive color/year from renewData if not yet set ---
  const getColorValue = useCallback(
    (currentInput) => {
      if (currentInput?.color) return currentInput.color;
      const rawColor = vehicleInfo.color;
      const trimmedColor = rawColor?.trim().toLowerCase();
      if (trimmedColor) {
        const foundColor = vehicleColor?.find(
          (color) => color?.colorName?.toLowerCase()?.trim() === trimmedColor,
        );
        return isNicon ? foundColor?.colorName : foundColor?.vehicleColorId;
      } else {
        const alternateColor = renewData?.vehicleColor?.toLowerCase()?.trim();
        const foundColor = vehicleColor?.find(
          (color) =>
            color?.colorName?.toLowerCase()?.trim() ===
            (trimmedColor || alternateColor),
        );
        return isNicon ? foundColor?.colorName : foundColor?.vehicleColorId;
      }
    },
    [isNicon, renewData, vehicleColor, vehicleInfo.color],
  );

  const getYearValue = useCallback(
    (currentInput) => {
      return (
        currentInput.year ||
        vehicleInfo.year ||
        ""
      );
    },
    [vehicleInfo.year],
  );

  useEffect(() => {
    const newColor = getColorValue(input);
    const newYear = getYearValue(input);

    if ((!input.color && newColor) || (!input.year && newYear)) {
      setInput((prev) => ({
        ...prev,
        color: prev.color || newColor,
        year: prev.year || newYear,
      }));
    }
    // eslint-disable-next-line
  }, [renewData, vehicleColor, vehicleModel]);

  // --- Fetch second insurance data (fallback for customer add) ---
  const fetchUserSecondInsuranceData = useCallback(
    async (plate_no) => {
      try {
        const getUserSecondInsuranceRes =
          await FixedAgent.getCustomerInsuranceOnlineData({
            plate_no,
            token,
          });

        const getUserRoadRes = await FixedAgent.getRoadData({
          plate_no: plate_no,
          token,
        });

        const addNewUser = await FixedAgent.addNewCustomer({
          phone_no: input.phone,
          plate_no: renewData?.plateNumber,
          fullname: getUserSecondInsuranceRes.data?.data?.full_name,
          token: token,
          engine_number: getUserSecondInsuranceRes.data?.data?.engine_no,
          chassis: getUserSecondInsuranceRes.data?.data?.chassis_no,
          make: getUserSecondInsuranceRes.data?.data?.make,
          model: getUserSecondInsuranceRes.data?.data?.model,
          year: getUserSecondInsuranceRes.data?.data?.year,
          road_expiry_date: getUserRoadRes.data.data.RwcExp,
          road_message: getUserRoadRes.data.data.message,
          color: getUserSecondInsuranceRes.data?.data?.color,
          state: "lagos",
        });

        setIsLoading(false);
        return addNewUser;
      } catch (err) {
        setIsLoading(false);

        if (err?.response?.data?.message === "Unauthenticated") {
          dispatch(userDataActions.logout());
          window.location.reload();
        }
        if (
          err?.response?.data?.message ===
          "Policy details are invalid or not matching."
        ) {
          dispatch(userDataActions.setUserPlateNumber(input.plate_no));
          setIsLoading(true);
          navigate("/add-customer");
        } else {
          showErrorToast(err?.response?.data?.message);
        }
      }
    },
    [
      token,
      dispatch,
      input.phone,
      renewData?.plateNumber,
      navigate,
      input.plate_no,
    ],
  );

  // --- Add customer handler ---
  const addCustomerHandler = useCallback(async () => {
    let customerRegistered = false;

    try {
      setIsLoading(true);
      const confirmedVehicle = getConfirmedCustomerVehicle();

      let getInsuranceRes = { data: { message: null, expiryDate: null } };
      try {
        const response = await FixedAgent.getInsuranceData({
          plate_no: input.plate_no,
          token,
        });

        if (response.status === 200) {
          getInsuranceRes = response;
        }
      } catch (error) {
        // Insurance data fetch failed, use defaults
      }

      const addNewUser = await FixedAgent.addNewCustomer({
        phone_no: input.phone,
        plate_no: input.plate_no,
        fullname:
          input.fullname ||
          renewData?.getNiconInsuranceRes?.insured_name ||
          renewData?.getNiconInsuranceRes?.full_name,
        email: input.email,
        token: token,
        engine_number:
          input.engineNo || renewData?.getNiconInsuranceRes?.engine_no,
        chassis: input.chasisNo ||
          vehicleInfo.chassis,
        make: confirmedVehicle.make,
        model: confirmedVehicle.model,
        year: input.year ||
          vehicleInfo.year,
        expiry_date: getInsuranceRes?.data?.message,
        color: confirmedVehicle.color,
        state: "lagos",
      });
      customerRegistered = true;

      await submitRenewHandler();

      setIsLoading(false);
      return addNewUser;
    } catch (err) {
      setIsLoading(false);
      if (!customerRegistered) {
        hasRetriedAfterCustomerRegistrationRef.current = false;
      }

      if (
        err?.response?.data?.message ===
        "Policy details are invalid or not matching."
      ) {
        setIsLoading(true);
        fetchUserSecondInsuranceData(input.plate_no);
      } else {
        showErrorToast(err?.response?.data?.message);
      }

      if (err?.response?.data?.message === "Unauthenticated") {
        dispatch(userDataActions.logout());
        window.location.reload();
      }
    }
    // eslint-disable-next-line
  }, [
    input,
    token,
    renewData,
    dispatch,
    fetchUserSecondInsuranceData,
    getConfirmedCustomerVehicle,
    vehicleInfo,
  ]);

  useEffect(() => {
    if (
      !isFgip ||
      !lookupVehicleModel ||
      !fgipModels.length ||
      input.model
    ) {
      return;
    }

    const matchedModel = fgipModels.find(
      (model) =>
        normalizeText(model.name) === normalizeText(lookupVehicleModel),
    );

    if (matchedModel) {
      setInput((previousInput) => ({
        ...previousInput,
        model: String(matchedModel.id),
      }));
    }
  }, [fgipModels, input.model, isFgip, lookupVehicleModel, setInput]);

  // --- Darak check handler ---
  const handleDarakCheck = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const darakResponse = await FixedAgent.checkDaraKStatus({
        plate_no: input.plate_no,
        token,
      });

      const darakData = darakResponse.data.data;
      setDarakCheckResult(darakData);

      if (darakData.is_darak) {
        const monthsRemaining = parseMonthsFromExpiration(
          darakData.policy_expiration_status,
        );

        if (monthsRemaining >= 10) {
          setShowWarningModal(true);
          setIsLoading(false);
          return;
        }
      }

      setModalOpen(true);
      setIsLoading(false);
    } catch (error) {
      setModalOpen(true);
      setIsLoading(false);
    }
  };

  // --- Submit renewal handler ---
  const submitRenewHandler = async (e) => {
    e?.preventDefault();

    if (isSubmittingRef.current) return;

    if (isFgiFamily && !renewData?.productsDetails?.id) {
      showErrorToast(
        `${renewData.insuranceType} is not configured for the selected vehicle type.`,
      );
      return;
    }

    isSubmittingRef.current = true;
    setIsLoading(true);
    try {
      let makeOrderRes;
      const rawPhone = input?.phone;
      const formattedPhone =
        "0" + String(rawPhone || "").replace(/\D/g, "").slice(-10);
      const confirmedVehicle = getConfirmedCustomerVehicle();

      await Auth.updateUserHandler({
          plateNumber: input.plate_no,
          fullname: input.fullname,
          email: input.email,
          phone_no: formattedPhone,
          date_of_birth: input.dob,
          gender: input.gender,
          occupation: input.occupation,
          address: input.address,
          chassis: input.chasisNo,
          engine_number: input.engineNo,
          make: confirmedVehicle.make,
          model: confirmedVehicle.model,
          year: input.year,
          token: token,
      });

      if (renewData.insuranceType === "NICON") {
        makeOrderRes = await FixedAgent.makeNiconManualInsuranceOrder({
          product_id: renewData?.productsDetails?.id,
          product_category: renewData?.productsDetails?.product_category,
          nicon_category_id: renewData?.productsDetails?.nicon_category_id,
          plate_no: input.plate_no,
          payment_option: renewData?.paymentOption,
          fullname: input.fullname,
          email: input.email,
          chassis: input.chasisNo,
          engine_number: input.engineNo,
          nicon_make_id: input.make,
          nicon_model_id: input.model,
          year: input.year,
          color: input.color,
          EffectiveDate: input.effectiveDate,
          token,
        });
      } else if (isFgi) {
        makeOrderRes = await FixedAgent.makeFgiManualInsuranceOrder({
          plate_no: input.plate_no,
          product_id: renewData?.productsDetails?.id,
          VehicleMakeId: input.make,
          VehicleModelId: input.model,
          VehicleColorId: input.color,
          SelectedVehicleMake: confirmedVehicle.make,
          SelectedVehicleModel: confirmedVehicle.model,
          SelectedVehicleColor:
            vehicleColor.find(
              (color) => Number(color.vehicleColorId) === Number(input.color),
            )?.colorName || input.color,
          EffectiveDate: currentDate,
          NIN: input.nin,
          fullname: input.fullname,
          email: input.email,
          chassis: input.chasisNo,
          engine_number: input.engineNo,
          year: input.year,
          token,
        });
      } else if (isFgip) {
        makeOrderRes = await FixedAgent.makeFgipManualInsuranceOrder({
          plate_no: input.plate_no,
          product_id: renewData?.productsDetails?.id,
          CategoryId: renewData?.productsDetails?.product_category,
          VehicleMakeId: input.make,
          VehicleModelId: input.model,
          VehicleColorId: input.color,
          SelectedVehicleMake: confirmedVehicle.make,
          SelectedVehicleModel: confirmedVehicle.model,
          SelectedVehicleColor:
            vehicleColor.find(
              (color) => Number(color.vehicleColorId) === Number(input.color),
            )?.colorName || input.color,
          EffectiveDate: currentDate,
          NIN: input.nin,
          fullname: input.fullname,
          email: input.email,
          chassis: input.chasisNo,
          engine_number: input.engineNo,
          year: input.year,
          token,
        });
      } else if (renewData.insuranceType === "SALAM") {
        makeOrderRes = await FixedAgent.makeSalamManualInsuranceOrder({
          manual_item_id: renewData?.productsDetails?.id,
          EffectiveDate: input.effectiveDate,
          VehicleMake: input.make,
          VehicleModel: input.model,
          VehicleColour: input.color,
          FullName: input.fullname,
          emailAddress: input.email,
          RegistrationNumber: input.plate_no,
          ChassisNumber: input.chasisNo,
          EngineNumber: input.engineNo,
          YearOfMake: input.year,
          NIN: input.nin,
          token,
        });
      } else if (isAg) {
        makeOrderRes = await FixedAgent.makeAGManualInsuranceOrder({
          product_id: renewData?.productsDetails?.id,
          plate_no: input.plate_no,
          payment_option: "PIN",
          EffectiveDate: input.effectiveDate,
          cboMakes: input.agVehicleMakeModelId,
          cboyear: input.year,
          cbocolor: input.color,
          chassis: input.chasisNo,
          engine_number: input.engineNo,
          cbovehicletype: input.agVehicleType,
          cboType: input.agPolicyType,
          fullname: input.fullname,
          email: input.email,
          token,
        });
      } else if (isAgp) {
        makeOrderRes = await FixedAgent.makeAGPManualInsuranceOrder({
          product_id: renewData?.productsDetails?.id,
          plate_no: input.plate_no,
          payment_option: "PIN",
          EffectiveDate: input.effectiveDate,
          cboMakes: input.make,
          cboModel: input.model,
          cboyear: input.year,
          cbocolor: input.color,
          chassis: input.chasisNo,
          engine_number: input.engineNo,
          cbovehicletype: input.agVehicleType,
          cboType: input.agPolicyType,
          fullname: input.fullname,
          email: input.email,
          token,
        });
      } else {
        makeOrderRes = await FixedAgent.makeCEIManualInsuranceOrder({
          product_id: renewData?.productsDetails?.id,
          plate_no: input.plate_no,
          payment_option: renewData?.paymentOption,
          fullname: input.fullname,
          email: input.email,
          chassis: input.chasisNo,
          engine_number: input.engineNo,
          cei_make_id: input.make,
          cei_model_id: input.model,
          year: input.year,
          cei_color_id: input.color,
          commercial_type: commercialType,
          token,
        });
      }

      showSuccessToast("Successful");

      const orderId =
        makeOrderRes?.data?.id ||
        makeOrderRes?.data?.data?.id ||
        makeOrderRes?.data?.order?.id;
      const policyNumber =
        makeOrderRes?.data?.policy_number ||
        makeOrderRes?.data?.data?.policy_number ||
        makeOrderRes?.data?.order?.policy_number;
      const plateNumber =     makeOrderRes?.data?.plate_no ||
        makeOrderRes?.data?.data?.plate_no ||
        makeOrderRes?.data?.order?.plate_no ||
        input.plate_no;
        console.log("plateNumber", plateNumber);

      if (isAgFamily && orderId && (policyNumber || plateNumber)) {
        dispatch(
          userDataActions.setRenewalPolicyReference({
            orderId,
            platform: renewData.insuranceType,
            policyNumber,
            plateNumber,
          }),
        );
      }

      if (orderId) {
        navigate(`/history/${orderId}`);
      } else {
        navigate("/history");
      }

      setIsLoading(false);
      isSubmittingRef.current = false;
      hasRetriedAfterCustomerRegistrationRef.current = false;
      return makeOrderRes;
    } catch (err) {
      const errorMessage = err?.response?.data?.message;

      if (
        errorMessage === "Plate not found!" ||
        errorMessage === "Customer not found!!!"
      ) {
        if (hasRetriedAfterCustomerRegistrationRef.current) {
          setIsLoading(false);
          isSubmittingRef.current = false;
          showErrorToast(
            "Customer registration completed, but the policy still could not be issued. Please contact support.",
          );
          return;
        }

        hasRetriedAfterCustomerRegistrationRef.current = true;
        setIsLoading(true);
        isSubmittingRef.current = false;
        addCustomerHandler();
        return;
      }

      setIsLoading(false);
      isSubmittingRef.current = false;

      // Focuswell can return HTTP 500 after accepting an FGIP policy request.
      // Treat this provider-specific response as completed and let the order
      // appear through the normal history refresh instead of asking the agent
      // to submit the policy again.
      if (isFgip && err?.response?.status === 500) {
        hasRetriedAfterCustomerRegistrationRef.current = false;
        showSuccessToast("Successful");
        navigate("/history");
        return { data: { success: true } };
      }

      if (isFgip) {
        const focuswellMessage = getFocuswellProviderMessage(err);
        if (focuswellMessage) {
          showErrorToast(focuswellMessage);
          return;
        }
      }

      if (
        err?.response?.data.error === "Request failed" ||
        err?.response?.data?.message === "Request failed"
      ) {
        // Send WhatsApp alert
        if (renewData.insuranceType === "NICON") {
          await FixedAgent.sendWhatsAppMessageToNonUser({
            phone_no: "09152647290",
            message: `This is from Nicon -- Policy type - ${
              renewData.productsDetails.pin_type
            }. -- Agent: ${user.fullname} - ${JSON.stringify(
              err.response.data,
            )}`,
            token: token,
          });
        } else if (renewData.insuranceType === "SALAM") {
          await FixedAgent.sendWhatsAppMessageToNonUser({
            phone_no: "09152647290",
            message: `This is from Salam -- Policy type - ${
              renewData.productsDetails.pin_type
            }. -- Agent: ${user.fullname} - ${JSON.stringify(
              err.response.data,
            )}`,
            token: token,
          });
        } else if (isAgFamily) {
          await FixedAgent.sendWhatsAppMessageToNonUser({
            phone_no: "09152647290",
            message: `This is from ${renewData.insuranceType} -- Policy type - ${
              renewData.productsDetails.pin_type
            }. -- Agent: ${user.fullname} - ${JSON.stringify(
              err.response.data,
            )}`,
            token: token,
          });
        } else if (isFgiFamily) {
          await FixedAgent.sendWhatsAppMessageToNonUser({
            phone_no: "09152647290",
            message: `This is from ${renewData.insuranceType} -- Policy type - ${
              renewData.productsDetails.pin_type
            }. -- Agent: ${user.fullname} - ${JSON.stringify(
              err.response.data,
            )}`,
            token: token,
          });
        } else {
          await FixedAgent.sendWhatsAppMessageToNonUser({
            phone_no: "09152647290",
            message: `This is from CEI -- Policy type - ${renewData.productsDetails.pin_type}. -- Agent: ${user.fullname} ${err.response.data.message}`,
            token: token,
          });
        }

        // Handle specific error messages
        const errorMsg =
          renewData.insuranceType === "NICON"
            ? err.response.data.error
            : err.response.data.message;

        if (errorMsg?.includes("Policy already created")) {
          showErrorToast("Policy already created");
        } else if (
          errorMsg?.includes("Insufficient credit. Contact your administrator")
        ) {
          showErrorToast("Insufficient credit. Contact your administrator");
        } else if (
          errorMsg?.includes(
            "Registration number already exist. Please ensure this is not a case of duplicated",
          )
        ) {
          showErrorToast(
            "Registration number already exists. Please ensure this is not a case of duplicated transaction.",
          );
        } else if (errorMsg?.includes("Wallet is exhausted.")) {
          showErrorToast("Wallet is exhausted.");
        } else if (
          errorMsg?.includes("One or more validation errors occurred.")
        ) {
          showErrorToast(
            "One or more validation errors occurred, Contact the management for support.",
          );
        } else {
          showErrorToast("System Error, Contact the management for support.");
        }
      } else {
        showErrorToast(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            "Renewal failed before provider request completed.",
        );
      }
    }
  };

  // --- Certificate URL ---
  const certUrl = `https://api.prod.myagentpro.ng/api/v1/app/fixedagent/customer/order/nicon/cert/${(
    input.plate_no ||
    renewData?.plateNumber ||
    ""
  ).toUpperCase()}`;

  // --- Render ---
  return (
    <div>
      <ToastContainer />

      <WarningModal
        isOpen={showWarningModal && !!darakCheckResult}
        onClose={() => setShowWarningModal(false)}
        certUrl={certUrl}
      />

      <DetailsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={submitRenewHandler}
        input={input}
        renewData={renewData}
        vehicleModel={vehicleModel}
        vehicleColor={vehicleColor}
        ceiMake={ceiMake}
        niconMake={niconMake}
        salamMake={salamMake}
        fgiMakes={vehicleMakes}
        agVehicleMakeModels={agVehicleMakeModels}
        agpVehicleMakes={agpVehicleMakes}
        agpVehicleModels={agpVehicleModels}
        agVehicleTypes={agVehicleTypes}
        agPolicyTypes={agPolicyTypes}
        isLoading={isLoading}
        isSubmitting={isSubmittingRef.current}
      />

      <Navbar title="Renew" />
      <div className={styles.body}>
        <form onSubmit={handleDarakCheck} className={styles.body_inner}>
          {/* Personal Details Section */}
          <div className={styles.wrapper}>
            <h1 className={styles.wrapper_title}>Confirm Your Details</h1>
            <div className={styles.input_container}>
              <input
                className={styles.input_field}
                type="text"
                name="fullname"
                required
                value={input.fullname}
                onChange={handleInputChange}
                placeholder="Full Name"
              />

              <input
                className={styles.input_field}
                type="text"
                required
                name="phone"
                onChange={handleInputChange}
                value={input.phone}
                placeholder="Phone Number"
              />
            </div>
          </div>

          {/* VIN/Chasis Section */}
          <div className={styles.wrapper}>
            <h1 className={styles.wrapper_title}>Enter Plate VIN to Begin!</h1>
            <div className={styles.input_container}>
              <input
                className={styles.input_field}
                type="text"
                required
                name="chasisNo"
                onChange={handleInputChange}
                value={input.chasisNo}
                placeholder="VIN/Chasis"
              />
            </div>
          </div>

          {/* Effective Date */}
          {(isNicon || isSalam || isAgFamily || isFgiFamily) && (
            <div className={styles.wrapper}>
              <h1 className={styles.wrapper_title}>Enter Effective Date</h1>
              <div className={styles.input_container}>
                <input
                  className={styles.input_field}
                  type="date"
                  required
                  name="effectiveDate"
                  onChange={isFgiFamily || isAg ? undefined : handleInputChange}
                  value={isFgiFamily || isAg ? currentDate : input.effectiveDate}
                  min={currentDate}
                  max={isAgp ? agMaxEffectiveDate : undefined}
                  disabled={isFgiFamily || isAg}
                />
              </div>
            </div>
          )}

          {/* Vehicle Form Fields */}
          <VehicleFormFields
            input={input}
            onInputChange={handleInputChange}
            renewData={renewData}
            vehicleInfo={vehicleInfo}
            vehicleModel={vehicleModel}
            vehicleColor={vehicleColor}
            ceiMake={ceiMake}
            niconMake={niconMake}
            salamMake={salamMake}
            fgiMakes={vehicleMakes}
            agVehicleMakeModels={agVehicleMakeModelOptions}
            agpVehicleMakes={agpVehicleMakes}
            agpVehicleModels={agpVehicleModelOptions}
            agVehicleTypeOptions={agVehicleTypeOptions}
            agPolicyTypeOptions={agPolicyTypeOptions}
            yearRange={yearRange}
            makeIdLoading={makeIdLoading}
            makeIdStatus={makeIdStatus}
            modelIdLoading={modelIdLoading}
            modelIdStatus={modelIdStatus}
            commercialType={commercialType}
            onCommercialTypeChange={(e) => setCommercialType(e.target.value)}
          />

          {isFgiFamily && fgiFormDataLoading && (
            <p className={styles.modal_description}>
              Loading {isFgip ? "FGIP colour" : "FGI vehicle"} data...
            </p>
          )}
          {isFgip && fgipDataLoading && (
            <p className={styles.modal_description}>Loading FGIP vehicle data...</p>
          )}
          {isFgip && fgipDataError && (
            <p className={styles.modal_description}>
              Unable to load FGIP vehicle data: {fgipDataError}
            </p>
          )}
          {isFgiFamily && fgiFormDataError && (
            <>
              <p className={styles.modal_description}>
                Unable to load {isFgip ? "FGIP colour" : "FGI vehicle"} data: {" "}
                {fgiFormDataError}
              </p>
              <button
                type="button"
                className={styles.modal_button}
                onClick={() => setFgiFormDataRetryKey((key) => key + 1)}
              >
                Retry FGI vehicle data
              </button>
            </>
          )}

          {/* Submit Button */}
          <button
            disabled={
              isLoading ||
              isSubmittingRef.current ||
              (isFgiFamily && (!fgiFormData || fgiFormDataLoading)) ||
              (isFgip && (fgipDataLoading || !fgipMakes.length))
            }
            style={{
              cursor:
                isLoading ||
                isSubmittingRef.current ||
                (isFgiFamily && (!fgiFormData || fgiFormDataLoading)) ||
                (isFgip && (fgipDataLoading || !fgipMakes.length))
                  ? "not-allowed"
                  : "pointer",
              opacity:
                isLoading ||
                isSubmittingRef.current ||
                (isFgiFamily && (!fgiFormData || fgiFormDataLoading)) ||
                (isFgip && (fgipDataLoading || !fgipMakes.length))
                  ? 0.5
                  : 1,
            }}
            className={styles.submit_button}
          >
            {isLoading ? "Loading..." : "Renew"}
          </button>
        </form>
      </div>
      <Footer />
    </div>
  );
}

export default RenewInfo;
